import { useState, useEffect } from 'react';
import {
  Zap,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MinusCircle,
  ChevronRight,
  ChevronDown,
  Clock,
  Shield,
  Target,
  Activity,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import type { BadgeVariant } from '../ui/Badge';
import { SkeletonCard } from '../ui/Skeleton';
import { getTriageSummary } from '../../services/enrichment';
import { formatDateTime, cn } from '../../lib/utils';
import type {
  TriageSummaryResponse,
  EnrichmentRunInfo,
  SignalMatch,
} from '../../data/types';

interface EnrichmentPanelProps {
  tenantId: string;
  incidentId: string;
}

const FAMILY_BADGES: Record<string, { label: string; variant: BadgeVariant }> = {
  identity: { label: 'Identity', variant: 'green' },
  email: { label: 'Email', variant: 'blue' },
  endpoint: { label: 'Endpoint', variant: 'orange' },
  generic: { label: 'Generic', variant: 'default' },
};

const STATUS_CONFIG: Record<string, { label: string; variant: BadgeVariant; Icon: typeof CheckCircle2 }> = {
  success: { label: 'Success', variant: 'green', Icon: CheckCircle2 },
  no_data: { label: 'No Data', variant: 'yellow', Icon: MinusCircle },
  unavailable: { label: 'Unavailable', variant: 'default', Icon: MinusCircle },
  skipped: { label: 'Skipped', variant: 'default', Icon: MinusCircle },
  error: { label: 'Error', variant: 'red', Icon: XCircle },
  timeout: { label: 'Timeout', variant: 'red', Icon: XCircle },
};

function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function confidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'bg-accent-green';
  if (confidence >= 0.6) return 'bg-accent-yellow';
  return 'bg-accent-red';
}

function confidenceBarBg(confidence: number): string {
  if (confidence >= 0.8) return 'bg-accent-green/20';
  if (confidence >= 0.6) return 'bg-accent-yellow/20';
  return 'bg-accent-red/20';
}

// ============================================================================
// PLAYBOOK ROUTING CARD
// ============================================================================

function PlaybookRoutingCard({ data }: { data: TriageSummaryResponse }) {
  const { routing, triageRun, enrichmentSummary } = data;
  const family = FAMILY_BADGES[routing.incidentFamily ?? 'generic'] ?? FAMILY_BADGES.generic;
  const confidence = routing.routingConfidence ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5 text-accent-blue" />
          Playbook Routing
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Playbook */}
          <div>
            <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Playbook</div>
            <div className="font-medium text-text-primary">
              {routing.playbookName || routing.playbookId || 'None'}
            </div>
            {routing.playbookDescription && (
              <div className="text-xs text-text-secondary mt-1 line-clamp-2">{routing.playbookDescription}</div>
            )}
          </div>

          {/* Family */}
          <div>
            <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Family</div>
            <Badge variant={family.variant}>{family.label}</Badge>
            {triageRun.usedFallback && (
              <Badge variant="yellow" className="ml-2">Fallback</Badge>
            )}
          </div>

          {/* Confidence */}
          <div>
            <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Routing Confidence</div>
            <div className="flex items-center gap-2">
              <div className={cn('h-2 rounded-full flex-1', confidenceBarBg(confidence))}>
                <div
                  className={cn('h-full rounded-full transition-all', confidenceColor(confidence))}
                  style={{ width: `${Math.round(confidence * 100)}%` }}
                />
              </div>
              <span className="text-sm font-medium text-text-primary w-10 text-right">
                {Math.round(confidence * 100)}%
              </span>
            </div>
          </div>

          {/* Duration */}
          <div>
            <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Total Duration</div>
            <div className="font-medium text-text-primary">{formatDuration(triageRun.durationMs)}</div>
            <div className="text-xs text-text-secondary mt-1">
              {enrichmentSummary.total} enrichments in {formatDuration(enrichmentSummary.totalDurationMs)}
            </div>
          </div>
        </div>

        {triageRun.needsHumanReview && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-md bg-accent-yellow/10 border border-accent-yellow/20">
            <AlertTriangle className="w-4 h-4 text-accent-yellow flex-shrink-0" />
            <span className="text-sm text-accent-yellow">Flagged for human review</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// SIGNAL SCORECARD
// ============================================================================

function SignalScorecard({ signals }: { signals: SignalMatch[] }) {
  if (signals.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent-blue" />
            Signal Scorecard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary">
            Signal details not available for this triage run.
          </p>
        </CardContent>
      </Card>
    );
  }

  const matchedCount = signals.filter(s => s.matched).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-accent-blue" />
          Signal Scorecard
          <span className="text-sm font-normal text-text-secondary ml-auto">
            {matchedCount} of {signals.length} matched
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-default">
                <th className="text-left py-2 pr-4 text-xs text-text-secondary font-medium uppercase tracking-wide">Type</th>
                <th className="text-left py-2 pr-4 text-xs text-text-secondary font-medium uppercase tracking-wide">Value</th>
                <th className="text-right py-2 pr-4 text-xs text-text-secondary font-medium uppercase tracking-wide w-20">Weight</th>
                <th className="text-center py-2 text-xs text-text-secondary font-medium uppercase tracking-wide w-16">Match</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((signal, i) => (
                <tr
                  key={`${signal.signalType}-${signal.signalValue}-${i}`}
                  className={cn(
                    'border-b border-border-default/50',
                    signal.matched ? 'bg-accent-green/5' : ''
                  )}
                >
                  <td className="py-2 pr-4">
                    <Badge variant="outline">{signal.signalType}</Badge>
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-text-primary break-all">
                    {signal.signalValue}
                  </td>
                  <td className="py-2 pr-4 text-right text-text-secondary">
                    {signal.weight.toFixed(1)}
                  </td>
                  <td className="py-2 text-center">
                    {signal.matched ? (
                      <CheckCircle2 className="w-4 h-4 text-accent-green inline-block" />
                    ) : (
                      <XCircle className="w-4 h-4 text-text-secondary/40 inline-block" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// ENRICHMENT ACTIONS
// ============================================================================

function EnrichmentRunRow({ run }: { run: EnrichmentRunInfo }) {
  const [expanded, setExpanded] = useState(false);
  const config = STATUS_CONFIG[run.status] ?? STATUS_CONFIG.error;
  const hasDetails = run.keyFindings.length > 0 || run.errorMessage;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  const categoryBadge = FAMILY_BADGES[run.presetCategory] ?? { label: run.presetCategory, variant: 'default' as BadgeVariant };

  return (
    <div className="border-b border-border-default/50 last:border-0">
      <button
        type="button"
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center gap-3 py-3 px-2 text-left transition-colors',
          hasDetails ? 'hover:bg-bg-tertiary/50 cursor-pointer' : 'cursor-default'
        )}
      >
        <Chevron className={cn('w-4 h-4 flex-shrink-0', hasDetails ? 'text-text-secondary' : 'text-transparent')} />
        <Badge variant={config.variant} className="flex-shrink-0">
          <config.Icon className="w-3 h-3" />
          {config.label}
        </Badge>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-text-primary truncate block">
            {run.presetName || run.presetId}
          </span>
        </div>
        <Badge variant={categoryBadge.variant} className="flex-shrink-0 hidden sm:inline-flex">
          {categoryBadge.label}
        </Badge>
        {run.requiredLicense && (
          <span className="text-xs text-text-secondary flex-shrink-0 hidden md:inline">
            {run.requiredLicense}
          </span>
        )}
        <span className="text-xs text-text-secondary flex-shrink-0 w-14 text-right">
          {formatDuration(run.durationMs)}
        </span>
        <span className="text-xs text-text-secondary flex-shrink-0 w-16 text-right">
          {run.rowCount !== null ? `${run.rowCount} rows` : '-'}
        </span>
      </button>

      {expanded && hasDetails && (
        <div className="px-9 pb-3 space-y-2">
          {run.keyFindings.length > 0 && (
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Key Findings</div>
              <ul className="space-y-1">
                {run.keyFindings.map((finding, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
                    <span className="text-accent-blue mt-0.5">-</span>
                    <span>{finding}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {run.errorMessage && (
            <div className="px-3 py-2 rounded bg-accent-red/10 border border-accent-red/20">
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">
                Error{run.errorCategory ? ` (${run.errorCategory})` : ''}
              </div>
              <div className="text-sm text-accent-red">{run.errorMessage}</div>
            </div>
          )}
          {run.presetDescription && (
            <div className="text-xs text-text-secondary">{run.presetDescription}</div>
          )}
        </div>
      )}
    </div>
  );
}

function EnrichmentActions({ runs, summary }: { runs: EnrichmentRunInfo[]; summary: TriageSummaryResponse['enrichmentSummary'] }) {
  const parts: string[] = [];
  if (summary.success > 0) parts.push(`${summary.success} success`);
  if (summary.noData > 0) parts.push(`${summary.noData} no data`);
  if (summary.unavailable > 0) parts.push(`${summary.unavailable} unavailable`);
  if (summary.skipped > 0) parts.push(`${summary.skipped} skipped`);
  if (summary.error > 0) parts.push(`${summary.error} error`);

  return (
    <Card padding="none">
      <div className="px-4 pt-4 mb-3">
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-accent-blue" />
          Enrichment Actions
          <span className="text-sm font-normal text-text-secondary ml-auto">
            {summary.total} actions: {parts.join(', ')}
          </span>
        </CardTitle>
      </div>
      <div className="px-2">
        {runs.length === 0 ? (
          <div className="px-4 pb-4 text-sm text-text-secondary">No enrichment actions were executed.</div>
        ) : (
          runs.map(run => <EnrichmentRunRow key={run.runId} run={run} />)
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// TRIAGE TIMELINE
// ============================================================================

interface TimelineStep {
  label: string;
  detail: string;
  timestamp: string | null;
  status: 'success' | 'error' | 'info';
}

function TriageTimeline({ data }: { data: TriageSummaryResponse }) {
  const { triageRun, routing, enrichmentRuns } = data;

  const steps: TimelineStep[] = [];

  steps.push({
    label: 'Triage Started',
    detail: `API ${triageRun.apiVersion}`,
    timestamp: triageRun.startedAt,
    status: 'info',
  });

  steps.push({
    label: 'Playbook Matched',
    detail: routing.playbookId
      ? `${routing.playbookName || routing.playbookId} (${Math.round((routing.routingConfidence ?? 0) * 100)}% confidence)`
      : 'No playbook matched',
    timestamp: triageRun.startedAt,
    status: routing.playbookId && routing.playbookId !== 'pb-generic' ? 'success' : 'info',
  });

  for (const run of enrichmentRuns) {
    const statusLabel = STATUS_CONFIG[run.status]?.label ?? run.status;
    const duration = run.durationMs !== null ? ` (${formatDuration(run.durationMs)})` : '';
    steps.push({
      label: run.presetName || run.presetId,
      detail: `${statusLabel}${duration}${run.rowCount !== null ? ` - ${run.rowCount} rows` : ''}`,
      timestamp: run.startedAt,
      status: run.status === 'success' || run.status === 'no_data' ? 'success' : run.status === 'error' || run.status === 'timeout' ? 'error' : 'info',
    });
  }

  if (triageRun.completedAt) {
    steps.push({
      label: 'Triage Completed',
      detail: `Total: ${formatDuration(triageRun.durationMs)}`,
      timestamp: triageRun.completedAt,
      status: triageRun.status === 'success' ? 'success' : 'error',
    });
  }

  const dotColor = (status: string) => {
    if (status === 'success') return 'bg-accent-green';
    if (status === 'error') return 'bg-accent-red';
    return 'bg-accent-blue';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-accent-blue" />
          Triage Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {steps.map((step, i) => (
            <div key={i} className="flex gap-3 pb-4 last:pb-0">
              {/* Vertical line + dot */}
              <div className="flex flex-col items-center">
                <div className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5', dotColor(step.status))} />
                {i < steps.length - 1 && (
                  <div className="w-px flex-1 bg-border-default mt-1" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pb-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium text-text-primary">{step.label}</span>
                  <span className="text-xs text-text-secondary">{step.detail}</span>
                </div>
                {step.timestamp && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3 text-text-secondary/60" />
                    <span className="text-xs text-text-secondary/60">{formatDateTime(step.timestamp)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN PANEL
// ============================================================================

export function EnrichmentPanel({ tenantId, incidentId }: EnrichmentPanelProps) {
  const [data, setData] = useState<TriageSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      const result = await getTriageSummary(tenantId, incidentId);

      if (cancelled) return;

      if (!result.success) {
        setError(result.error || 'Failed to load enrichment data');
      } else {
        setData(result.data);
      }
      setIsLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [tenantId, incidentId]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <XCircle className="w-12 h-12 text-accent-red mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">Error loading enrichment data</h3>
          <p className="text-text-secondary">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Zap className="w-12 h-12 text-text-secondary/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No enrichment data</h3>
          <p className="text-text-secondary">
            This incident was not processed through the automated triage pipeline.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PlaybookRoutingCard data={data} />
      <SignalScorecard signals={data.routing.signalMatches} />
      <EnrichmentActions runs={data.enrichmentRuns} summary={data.enrichmentSummary} />
      <TriageTimeline data={data} />
    </div>
  );
}
